import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { useTranslate } from "react-admin";
import InfoIcon from "@material-ui/icons/Info";
import { startOfDay } from "date-fns";
import { PeriodType } from "../../../common/data/service-proxies";
import { useHarshEventDailyLogs } from "../hooks/useHarshDrivingReportsHooks";
import type { HarshDrivingReportsHookParams } from "../hooks/useHarshDrivingReportsHooks";
import { useProLogsTheme } from "../../../common/hooks/useProLogsTheme";
import { formatTooltipAxis } from "../fuel-economy-report/chartUtils";
import ReactECharts from "echarts-for-react";
import "../fuel-economy-report/FuelEconomyReport.css";

const buildDateLabels = (
  from: Date,
  to: Date,
  periodType: PeriodType,
): string[] => {
  const result: string[] = [];
  const fromStart = startOfDay(from);
  const toStart = startOfDay(to);
  const oneDay = 24 * 60 * 60 * 1000;

  if (periodType === PeriodType.Weekly) {
    for (let d = fromStart.getTime(); d <= toStart.getTime(); d += oneDay) {
      result.push(
        new Date(d).toLocaleDateString("en-US", { weekday: "short" }),
      );
    }
  } else {
    const days: Date[] = [];
    for (let d = fromStart.getTime(); d <= toStart.getTime(); d += oneDay) {
      days.push(new Date(d));
    }
    const byWeek: { [bucket: string]: number } = {};
    days.forEach((d) => {
      const dayOfMonth = d.getDate();
      const bucket = `${Math.floor((dayOfMonth - 1) / 7) + 1}`;
      byWeek[bucket] = 1;
    });
    Object.keys(byWeek)
      .sort((a, b) => Number(a) - Number(b))
      .forEach((b) => result.push(`W${b}`));
  }

  return result;
};

export const HarshEventsWidget: React.FC<HarshDrivingReportsHookParams> = ({
  fromDate,
  toDate,
  periodType,
}) => {
  const translate = useTranslate();
  const theme = useProLogsTheme();
  const { data, loading: isLoading, error } = useHarshEventDailyLogs({
    fromDate,
    toDate,
    periodType,
  });
  const isError = !!error;

  const chartOption = useMemo(() => {
    if (!data) return null;
    const labels = buildDateLabels(
      fromDate,
      toDate,
      periodType ?? PeriodType.Weekly,
    );
    const morningValues = data.morningValues ?? [];
    const noonValues = data.noonValues ?? [];
    const afternoonValues = data.afternoonValues ?? [];
    const nightValues = data.nightValues ?? [];

    const hasData =
      morningValues.length > 0 ||
      noonValues.length > 0 ||
      afternoonValues.length > 0 ||
      nightValues.length > 0;
    if (!hasData) return null;

    return {
      tooltip: {
        trigger: "axis",
        formatter: (params: unknown) => formatTooltipAxis(params),
      },
      legend: {
        data: [
          translate("harshDrivingReport.morning", { _: "Morning" }),
          translate("harshDrivingReport.noon", { _: "Noon" }),
          translate("harshDrivingReport.afternoon", { _: "Afternoon" }),
          translate("harshDrivingReport.night", { _: "Night" }),
        ],
        bottom: 0,
        textStyle: {
          color: theme.palette.text.primary,
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "15%",
        top: "15%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: labels,
        boundaryGap: false,
        axisLabel: {
          color: theme.palette.text.primary,
        },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          color: theme.palette.text.primary,
        },
      },
      series: [
        {
          name: translate("harshDrivingReport.morning", { _: "Morning" }),
          type: "line",
          data: morningValues,
          smooth: false,
          itemStyle: { color: "#8b5cf6" },
          symbol: "circle",
          symbolSize: 6,
        },
        {
          name: translate("harshDrivingReport.noon", { _: "Noon" }),
          type: "line",
          data: noonValues,
          smooth: false,
          itemStyle: { color: "#ec4899" },
          symbol: "circle",
          symbolSize: 6,
        },
        {
          name: translate("harshDrivingReport.afternoon", { _: "Afternoon" }),
          type: "line",
          data: afternoonValues,
          smooth: false,
          itemStyle: { color: "#38bdf8" },
          symbol: "circle",
          symbolSize: 6,
        },
        {
          name: translate("harshDrivingReport.night", { _: "Night" }),
          type: "line",
          data: nightValues,
          smooth: false,
          itemStyle: { color: "#f97316" },
          symbol: "circle",
          symbolSize: 6,
        },
      ],
    };
  }, [
    data,
    fromDate,
    toDate,
    periodType,
    translate,
    theme.palette.text.primary,
  ]);

  const hasData = useMemo(() => {
    if (!data) return false;
    const morning = data.morningValues?.length ?? 0;
    const noon = data.noonValues?.length ?? 0;
    const afternoon = data.afternoonValues?.length ?? 0;
    const night = data.nightValues?.length ?? 0;
    return morning > 0 || noon > 0 || afternoon > 0 || night > 0;
  }, [data]);

  return (
    <Card className="widget">
      <div className="widget-title">
        <div className="widget-title-icon" style={{ color: "#1976d2" }}>
          <InfoIcon />
        </div>
        <div className="widget-title-label">
          {translate("harshDrivingReport.harshEvents", {
            _: "Harsh events",
          })}
        </div>
      </div>
      <CardContent
        style={{
          padding: 16,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <Typography variant="body2" color="error">
              {translate("common.errorLoadingData", {
                _: "Error loading data",
              })}
            </Typography>
          </Box>
        ) : !hasData ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <Typography variant="body2" color="textSecondary">
              {translate("common.noData", {
                _: "No data",
              })}
            </Typography>
          </Box>
        ) : chartOption ? (
          <Box flex={1} minHeight={300} style={{ minHeight: 0, width: "100%" }}>
            <ReactECharts
              option={chartOption}
              style={{ height: "100%", width: "100%", minHeight: 300 }}
            />
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default HarshEventsWidget;
